/* SpawnAnything — destructible terrain: explosions carve craters into the heightfield (pure, worker-safe).
   A carved cell gets a flat top at an absolute height (t.cut / t.cutH, read by heightAt and the step test in
   terrain.js); t.cutO keeps the original height, t.cutW the water level + 1 of a flooded crater cell.
   Craters are bowls (rim at the ground height of the blast centre) plus a 0.25 m/m cone outside that cuts
   hills and terrace edges down to a walkable slope. Only +, -, *, /, sqrt and floor: bit-identical everywhere.
   The worker sends every crater as [x, z, R, D]; the client replays them with craterStamp on its own copy. */
import { cellAt, createEmptyTerrain, heightAt, inBounds, isWalkableCell, canStep } from '../../world/terrain.js';
import { DX8, DZ8 } from '../flowfield.js';

export const CRATER_CHUNK = 16;          // cells per chunk side (dirty tracking for passability and meshes)
const SLOPE = 0.25;                      // m per m: cone outside the bowl, walkable (half a step per 2 m cell)
const MAXD = 8;                          // deepest total cut below the original ground (m)
const CUT_EPS = 0.03, FLOOD_EPS = 0.05;
const PASS_EVERY = 6, PASS_CHUNKS = 12;  // passability refresh: every 0.2 s, at most this many chunks
const REC_MAX = 400000;

/** Worker-side state: option, records for the client (rec: not sent yet, all: this battle, for a resync after a
    replay seek), per-cell speed and cover factors. */
export function craterState() {
  return { on: false, t: null, rec: [], all: [], resync: false, slow: null, cover: null };
}

const wet = (t, i) => t.water[i] > 0 || (t.cutW && t.cutW[i] > 0);
/** Height of a cell's top at its centre (carved, ramp or flat). */
export function cellTop(t, i) {
  if (t.cut && t.cut[i]) return t.cutH[i];
  const rd = t.rampDir[i];
  return rd >= 0 ? (t.level[i] + (t.rampK[i] + 0.5) / t.rampLen) * t.stepHeight : t.level[i] * t.stepHeight;
}
function craterInit(t) {
  if (t.cut) return;
  const N = t.n * t.n, nc = Math.ceil(t.n / CRATER_CHUNK);
  t.cut = new Uint8Array(N); t.cutH = new Float32Array(N); t.cutO = new Float32Array(N); t.cutW = new Uint8Array(N);
  t.cutQ = new Int32Array(N); t.cutDirty = new Uint8Array(nc * nc); t.cutDirtyN = 0;
}
function markRect(t, x0, z0, x1, z1) {
  const n = t.n, nc = Math.ceil(n / CRATER_CHUNK), D = t.cutDirty;
  x0 = x0 < 0 ? 0 : x0; z0 = z0 < 0 ? 0 : z0; x1 = x1 >= n ? n - 1 : x1; z1 = z1 >= n ? n - 1 : z1;
  for (let cz = (z0 / CRATER_CHUNK) | 0, ez = (z1 / CRATER_CHUNK) | 0; cz <= ez; cz++)
    for (let cx = (x0 / CRATER_CHUNK) | 0, ex = (x1 / CRATER_CHUNK) | 0; cx <= ex; cx++) {
      const k = cz * nc + cx; if (!D[k]) { D[k] = 1; t.cutDirtyN++; }
    }
}
// highest water level among the wet 4-neighbours of cell i (river, bridge or flooded crater), -1 = none
function wetLevel(t, i) {
  const n = t.n, gx = i % n, gz = (i / n) | 0; let L = -1;
  for (let d = 0; d < 8; d += 2) {
    const x = gx + DX8[d], z = gz + DZ8[d]; if (x < 0 || z < 0 || x >= n || z >= n) continue;
    const j = z * n + x; if (!wet(t, j)) continue;
    const l = t.cutW[j] > 0 ? t.cutW[j] - 1 : t.waterLevel[j];
    if (l > L) L = l;
  }
  return L;
}
function flood(t, i, L, sim) {
  t.cutW[i] = L + 1;
  if (sim) { t.water[i] = 1; t.waterLevel[i] = L; } // the simulation treats it as river water (impassable, water surface)
}

/**
 * Carve a crater of radius R and depth D at (x, z). sim = true also marks flooded cells as water for the
 * simulation (the client keeps its own terrain arrays untouched and reads t.cutW). Returns true if anything changed.
 */
export function craterStamp(t, x, z, R, D, sim) {
  if (!inBounds(t, x, z)) return false;
  const c0 = cellAt(t, x, z);
  if (t.water[c0] > 0 || (t.cutW && t.cutW[c0] > 0)) return false; // into water: no crater
  craterInit(t);
  const n = t.n, cell = t.cell, o = t.origin, Q = t.cutQ;
  // the cone reaches back up to the original ground of the centre, so hitting the same spot again keeps the walls walkable
  const h0 = cellTop(t, c0), oc = t.cut[c0] ? t.cutO[c0] : h0, R2 = R * R, md = D * 2.5 < MAXD ? D * 2.5 : MAXD;
  const Ro = R + Math.max(R * 0.6, (oc - h0) / SLOPE + 1);
  let gx0 = Math.floor((x - Ro - o) / cell), gx1 = Math.floor((x + Ro - o) / cell);
  let gz0 = Math.floor((z - Ro - o) / cell), gz1 = Math.floor((z + Ro - o) / cell);
  if (gx0 < 0) gx0 = 0; if (gz0 < 0) gz0 = 0; if (gx1 > n - 1) gx1 = n - 1; if (gz1 > n - 1) gz1 = n - 1;
  let q = 0;
  for (let gz = gz0; gz <= gz1; gz++) {
    const dz = o + (gz + 0.5) * cell - z;
    for (let gx = gx0; gx <= gx1; gx++) {
      const i = gz * n + gx; if (wet(t, i)) continue;
      const dx = o + (gx + 0.5) * cell - x, d2 = dx * dx + dz * dz;
      if (d2 >= Ro * Ro) continue;
      let h = d2 < R2 ? h0 - D * (1 - d2 / R2) : h0 + (Math.sqrt(d2) - R) * SLOPE;
      const cur = cellTop(t, i);
      if (h >= cur - CUT_EPS) continue;
      const org = t.cut[i] ? t.cutO[i] : cur;
      if (h < org - md) h = org - md;
      if (h >= cur - CUT_EPS) continue;
      if (!t.cut[i]) { t.cut[i] = 1; t.cutO[i] = cur; }
      t.cutH[i] = h; Q[q++] = i;
    }
  }
  if (!q) return false;
  markRect(t, gx0 - 2, gz0 - 2, gx1 + 2, gz1 + 2);
  // flooding: lowered cells next to water fill up to its surface, and so do carved cells next to them
  const sh = t.stepHeight, F = Q; let qh = 0, qt = 0;
  for (let k = 0; k < q; k++) {
    const i = Q[k]; if (t.cutW[i]) continue;
    const L = wetLevel(t, i);
    if (L >= 0 && t.cutH[i] < L * sh - t.waterDrop - FLOOD_EPS) { flood(t, i, L, sim); F[qt++] = i; }
  }
  while (qh < qt) {
    const i = F[qh++], L = t.cutW[i] - 1, gx = i % n, gz = (i / n) | 0, S = L * sh - t.waterDrop - FLOOD_EPS;
    markRect(t, gx - 2, gz - 2, gx + 2, gz + 2);
    for (let d = 0; d < 8; d += 2) {
      const x2 = gx + DX8[d], z2 = gz + DZ8[d]; if (x2 < 0 || z2 < 0 || x2 >= n || z2 >= n) continue;
      const j = z2 * n + x2;
      if (!t.cut[j] || wet(t, j) || t.cutH[j] >= S) continue;
      flood(t, j, L, sim); F[qt++] = j;
    }
  }
  return true;
}

/** Undo all craters on t (sim = true: also give flooded cells back to the land). Returns true if t had any. */
export function craterClear(t, sim) {
  if (!t || !t.cut) return false;
  if (sim) for (let i = 0; i < t.cutW.length; i++) if (t.cutW[i]) { t.water[i] = 0; t.waterLevel[i] = 0; }
  t.cut = t.cutH = t.cutO = t.cutW = t.cutQ = t.cutDirty = null; t.cutDirtyN = 0;
  return true;
}

/** Flat heightfield for the flat field (no terrain): covers ±W, made on the first crater. */
export function craterFlatGrid(n, cell) {
  const t = createEmptyTerrain({ n, cell, stepHeight: 1.2, levels: 5 });
  t.craterFlat = true;
  return t;
}
export function craterFlatTerrain(W) {
  let cell = 2;
  while (2 * Math.ceil((W + 2) / cell) > 1024) cell *= 2;
  return craterFlatGrid(2 * Math.ceil((W + 2) / cell), cell);
}

/* ---------------- worker side ---------------- */
/** New battle: take the option from the setup message, drop records; true = terrain must be reset (rebuild passability / drop the flat grid). */
export function craterReset(CR, msg, t) {
  CR.on = !!(msg && msg.crater); // every setup carries the option (absent = off)
  CR.rec.length = 0; CR.all.length = 0; CR.resync = false; CR.t = null; CR.slow = CR.cover = null;
  if (!t) return false;
  if (t.craterFlat) return true;
  return craterClear(t, true);
}

/** Crater size from an explosion: radius from the blast radius, depth from blast radius and damage (granate small, atom huge). */
export function craterSize(aoe, dmg, kind, out) {
  if (kind === 2 || kind === 4) return false; // rubber chickens and stink clouds leave the ground alone
  if (!(aoe > 0) || !(dmg > 0)) return false;
  if (aoe > 80) aoe = 80;
  const R = aoe * 0.65;
  if (R < 1.3) return false;
  const sd = Math.sqrt(dmg < 1e7 ? dmg : 1e7), p = sd / (sd + 12);
  let D = aoe * 0.14 * p;
  if (D > R * 0.14) D = R * 0.14; // steepest bowl wall stays walkable
  if (D < 0.08) return false;
  out[0] = R; out[1] = D;
  return true;
}
const SZ = new Float64Array(2);
/** Explosion at (x, y, z): carve (unless it bursts high in the air) and record it for the client. */
export function craterExplosion(CR, t, x, y, z, aoe, dmg, kind) {
  if (!craterSize(aoe, dmg, kind, SZ) || !inBounds(t, x, z)) return false;
  if (y - heightAt(t, x, z) > aoe * 0.5 + 1) return false; // air burst
  if (!craterStamp(t, x, z, SZ[0], SZ[1], true)) return false;
  if (CR.t !== t) { CR.t = t; CR.slow = CR.cover = null; }
  if (CR.rec.length < REC_MAX) CR.rec.push(x, z, SZ[0], SZ[1]);
  if (CR.all.length < REC_MAX) CR.all.push(x, z, SZ[0], SZ[1]);
  return true;
}

// passability of the cells around a chunk (same rules as buildPassability in flowfield.js)
function repass(t, pass, x0, z0, x1, z1) {
  const n = t.n, mask = pass.mask, walk = pass.walk;
  const ax0 = x0 > 0 ? x0 - 1 : 0, az0 = z0 > 0 ? z0 - 1 : 0, ax1 = x1 < n - 1 ? x1 + 1 : n - 1, az1 = z1 < n - 1 ? z1 + 1 : n - 1;
  for (let gz = az0; gz <= az1; gz++) for (let gx = ax0; gx <= ax1; gx++) { const i = gz * n + gx; walk[i] = isWalkableCell(t, i) ? 1 : 0; }
  // orthogonal moves of the chunk and its ring (the ring's steps into the chunk may have changed)
  for (let gz = az0; gz <= az1; gz++) for (let gx = ax0; gx <= ax1; gx++) {
    const i = gz * n + gx;
    if (!walk[i]) { mask[i] = 0; continue; }
    let m = 0;
    if (gx < n - 1 && canStep(t, i, i + 1)) m |= 1;
    if (gz < n - 1 && canStep(t, i, i + n)) m |= 4;
    if (gx > 0 && canStep(t, i, i - 1)) m |= 16;
    if (gz > 0 && canStep(t, i, i - n)) m |= 64;
    mask[i] = m;
  }
  // diagonals of the chunk and its ring: only when both orthogonal detours are open
  for (let gz = az0; gz <= az1; gz++) for (let gx = ax0; gx <= ax1; gx++) {
    const i = gz * n + gx, m = mask[i] & 0x55; if (!m) { mask[i] = 0; continue; }
    let add = 0;
    for (let d = 1; d < 8; d += 2) {
      const dx = DX8[d], dz = DZ8[d], bx = dx > 0 ? 0 : 4, bz = dz > 0 ? 2 : 6;
      if (!(m & (1 << bx)) || !(m & (1 << bz))) continue;
      const ox = i + dx, oz = i + dz * n;
      if ((mask[ox] & (1 << bz)) && (mask[oz] & (1 << bx))) add |= 1 << d;
    }
    mask[i] = m | add;
  }
}
// speed (slope, loose earth) and cover factors of the cells of a chunk
function terrainFx(CR, t, x0, z0, x1, z1) {
  const n = t.n, cell = t.cell, S = CR.slow, C = CR.cover, cut = t.cut;
  for (let gz = z0; gz <= z1; gz++) for (let gx = x0; gx <= x1; gx++) {
    const i = gz * n + gx;
    let near = cut[i] > 0;
    for (let d = 0; d < 8 && !near; d += 2) { const x = gx + DX8[d], z = gz + DZ8[d]; if (x >= 0 && z >= 0 && x < n && z < n && cut[z * n + x]) near = true; }
    if (!near || wet(t, i)) { S[i] = 1; C[i] = 1; continue; }
    const h = cellTop(t, i); let s = 0;
    for (let d = 0; d < 8; d += 2) {
      const x = gx + DX8[d], z = gz + DZ8[d]; if (x < 0 || z < 0 || x >= n || z >= n) continue;
      const j = z * n + x; if (wet(t, j)) continue;
      const dh = Math.abs(cellTop(t, j) - h); if (dh > s) s = dh;
    }
    let f = 1 - Math.min(0.5, s / cell * 1.6);
    if (cut[i]) f *= 0.92;
    S[i] = f;
    C[i] = cut[i] ? 1 - Math.min(0.45, (t.cutO[i] - t.cutH[i]) * 0.3) : 1;
  }
}
/** Throttled refresh after craters: passability (flow fields pick it up at their next recompute), speed and cover. */
export function craterSync(CR, t, pass, stepN) {
  if (!t.cutDirtyN || (stepN % PASS_EVERY) !== 0) return false;
  const N = t.n * t.n;
  if (!CR.slow || CR.slow.length !== N) { CR.slow = new Float32Array(N).fill(1); CR.cover = new Float32Array(N).fill(1); CR.t = t; }
  const n = t.n, nc = Math.ceil(n / CRATER_CHUNK), D = t.cutDirty;
  let done = 0;
  for (let k = 0; k < D.length && done < PASS_CHUNKS; k++) {
    if (!D[k]) continue;
    D[k] = 0; t.cutDirtyN--; done++;
    const x0 = (k % nc) * CRATER_CHUNK, z0 = ((k / nc) | 0) * CRATER_CHUNK;
    const x1 = Math.min(n, x0 + CRATER_CHUNK) - 1, z1 = Math.min(n, z0 + CRATER_CHUNK) - 1;
    repass(t, pass, x0, z0, x1, z1);
    terrainFx(CR, t, x0, z0, x1, z1);
  }
  return true;
}
/** Speed factor of the ground under (x, z): crater slopes and loose earth slow units down. */
export function craterSlow(CR, t, x, z) { return CR.slow[cellAt(t, x, z)]; }
/** Hit factor against a target at (x, z): units down in a crater are harder to hit from afar. */
export function craterCover(CR, t, x, z) { return CR.cover ? CR.cover[cellAt(t, x, z)] : 1; }

/** Pending crater records as a message for the client (null when there is nothing new). */
export function craterFlush(CR, gen) {
  if (!CR.rec.length || !CR.t) return null;
  const rec = Float64Array.from(CR.rec); CR.rec.length = 0;
  const t = CR.t, reset = CR.resync; CR.resync = false;
  return { type: 'crater', gen, rec, reset, flat: t.craterFlat ? { n: t.n, cell: t.cell } : null };
}
/** After a replay seek / snapshot load: send every crater of this battle again (the client starts from scratch). */
export function craterResync(CR, t) {
  if (!CR.all.length || !t) return;
  CR.rec = CR.all.slice(); CR.t = t; CR.resync = true;
}
